import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/Dashboard/DashboardLayout';
import { AIAssistant } from '@/components/AI';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { useExecutiveKPIs, SmartAlert } from '@/hooks/useExecutiveKPIs';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Target,
  Zap,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Gauge,
  Boxes,
  Wrench,
  ShoppingCart,
  ExternalLink,
  ClipboardList,
  Loader2,
  Wallet,
  Package,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { canViewMoney } from '@/lib/roleUtils';
import { format, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { exportToPDF, exportToExcel, formatters } from '@/lib/exportUtils';

// Types for KPI Cards
type KPIFormat = 'currency' | 'percent' | 'number';

const KPICard = ({
  title,
  value,
  previousValue,
  target,
  format: formatType = 'number',
  icon: Icon,
  color = 'primary'
}: {
  title: string;
  value: number;
  previousValue: number;
  target: number;
  format?: 'currency' | 'percent' | 'number';
  icon: any;
  color?: string;
}) => {
  const change = ((value - previousValue) / previousValue) * 100;
  const isPositive = change > 0;
  const progressToTarget = (value / target) * 100;

  const formatValue = (v: number) => {
    if (formatType === 'currency') return `${(v / 1000).toFixed(0)}K`;
    if (formatType === 'percent') return `${v.toFixed(1)}%`;
    return v.toLocaleString();
  };

  return (
    <Card className="relative overflow-hidden">
      <div className={cn(
        "absolute top-0 right-0 w-32 h-32 -mr-8 -mt-8 rounded-full opacity-10",
        color === 'primary' && "bg-primary",
        color === 'green' && "bg-green-500",
        color === 'orange' && "bg-orange-500",
        color === 'blue' && "bg-blue-500",
      )} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-1">
              {formatType === 'currency' && '€'}{formatValue(value)}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={isPositive ? "default" : "destructive"} className="gap-1">
                {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(change).toFixed(1)}%
              </Badge>
              <span className="text-xs text-muted-foreground">vs mes anterior</span>
            </div>
          </div>
          <div className={cn(
            "p-3 rounded-xl",
            color === 'primary' && "bg-primary/10 text-primary",
            color === 'green' && "bg-green-500/10 text-green-500",
            color === 'orange' && "bg-orange-500/10 text-orange-500",
            color === 'blue' && "bg-blue-500/10 text-blue-500",
          )}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs mb-1">
            <span className="text-muted-foreground">Progreso al objetivo</span>
            <span className="font-medium">{progressToTarget.toFixed(0)}%</span>
          </div>
          <Progress value={Math.min(progressToTarget, 100)} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            Objetivo: {formatType === 'currency' && '€'}{formatValue(target)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const OEEGauge = ({ value, label, color }: { value: number; label: string; color: string }) => (
  <div className="text-center">
    <div className="relative w-24 h-24 mx-auto">
      <svg className="w-24 h-24 transform -rotate-90">
        <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="8" fill="none" className="text-muted/20" />
        <circle
          cx="48" cy="48" r="40"
          stroke={color}
          strokeWidth="8"
          fill="none"
          strokeDasharray={`${(value / 100) * 251.2} 251.2`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold">{value.toFixed(1)}%</span>
      </div>
    </div>
    <p className="text-sm text-muted-foreground mt-2">{label}</p>
  </div>
);

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userRole } = useAuth();
  const canSeeMoney = canViewMoney(userRole);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');

  // Fetch real data from database
  const { data: kpiData, isLoading, refetch, isRefetching } = useExecutiveKPIs(timeRange);

  // Estados para diálogos de alertas
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<SmartAlert | null>(null);

  // Estados para formularios
  const [orderQuantity, setOrderQuantity] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [maintenanceDate, setMaintenanceDate] = useState('');
  const [maintenanceNotes, setMaintenanceNotes] = useState('');

  const handleRefresh = () => {
    refetch();
  };

  const handleExport = (type: 'pdf' | 'excel') => {
    if (!kpiData) {
      toast({
        title: 'Error',
        description: 'No hay datos para exportar',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Prepare KPI data for export
      const wo = kpiData.financial.workOrders;
      const exportData = [
        { metric: 'Ordenes Totales', value: wo.total, unit: 'unidades' },
        { metric: 'Ordenes Completadas', value: wo.completed, unit: 'unidades' },
        { metric: 'Ordenes En Progreso', value: wo.inProgress, unit: 'unidades' },
        { metric: 'Ordenes Pendientes', value: wo.pending, unit: 'unidades' },
        { metric: 'Tasa de Completado', value: Number(wo.completionRate || 0).toFixed(1), unit: '%' },
        { metric: 'Entrega a Tiempo', value: Number(wo.onTimeRate || 0).toFixed(1), unit: '%' },
        { metric: 'Tasa de Calidad', value: Number(kpiData.quality.passRate || 0).toFixed(1), unit: '%' },
        { metric: 'Inspecciones', value: kpiData.quality.totalInspections, unit: 'unidades' },
        { metric: 'Defectos', value: kpiData.quality.totalDefects, unit: 'unidades' },
        { metric: 'Valor Inventario', value: Number(kpiData.inventory.totalInventoryValue || 0).toLocaleString('es-ES'), unit: 'EUR' },
      ];

      const columns = [
        { key: 'metric', header: 'Metrica', width: 30 },
        { key: 'value', header: 'Valor', width: 18 },
        { key: 'unit', header: 'Unidad', width: 12 },
      ];

      const options = {
        filename: 'dashboard_ejecutivo',
        title: 'Dashboard Ejecutivo - Athenea MES',
        subtitle: `Generado el ${format(new Date(), "d 'de' MMMM yyyy", { locale: es })}`,
        columns,
        data: exportData,
        dateRange: { start: subDays(new Date(), 30), end: new Date() },
      };

      if (type === 'pdf') {
        exportToPDF(options);
      } else {
        exportToExcel(options);
      }

      toast({
        title: 'Exportacion completada',
        description: `Reporte exportado en formato ${type.toUpperCase()}`,
      });
    } catch (error) {
      console.error("Error exportando:", error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el reporte',
        variant: 'destructive',
      });
    }
  };

  // Manejador de acciones de alertas
  const handleAlertAction = (alert: SmartAlert) => {
    setSelectedAlert(alert);

    switch (alert.actionType) {
      case 'navigate':
        if (alert.navigateTo) {
          toast({
            title: 'Navegando',
            description: `Redirigiendo a ${alert.workOrderCode || alert.navigateTo}...`,
          });
          navigate(alert.navigateTo);
        }
        break;

      case 'create_order':
        setOrderQuantity('');
        setOrderNotes('');
        setOrderDialogOpen(true);
        break;

      case 'schedule_maintenance':
        setMaintenanceDate('');
        setMaintenanceNotes('');
        setMaintenanceDialogOpen(true);
        break;

      case 'view_details':
        if (alert.navigateTo) {
          toast({
            title: 'Navegando',
            description: 'Redirigiendo a detalles de producción...',
          });
          navigate(alert.navigateTo);
        }
        break;
    }
  };

  // Crear pedido de material
  const handleCreateOrder = () => {
    if (!orderQuantity) {
      toast({
        title: 'Error',
        description: 'Ingrese la cantidad a pedir',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Pedido generado',
      description: `Se ha generado un pedido de ${orderQuantity} unidades de ${selectedAlert?.materialName || 'material'}`,
    });

    setOrderDialogOpen(false);
    setSelectedAlert(null);
  };

  // Programar mantenimiento
  const handleScheduleMaintenance = () => {
    if (!maintenanceDate) {
      toast({
        title: 'Error',
        description: 'Seleccione una fecha para el mantenimiento',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Mantenimiento programado',
      description: `Se ha programado el mantenimiento de ${selectedAlert?.machineCode || 'equipo'} para el ${maintenanceDate}`,
    });

    setMaintenanceDialogOpen(false);
    setSelectedAlert(null);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Dashboard Ejecutivo
            </h1>
            <p className="text-muted-foreground mt-1">
              Visión integral del rendimiento empresarial en tiempo real
            </p>
          </div>
          <AIAssistant moduleId="dashboard" position="compact" className="mb-4" />
            <div className="flex items-center gap-3">
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Esta semana</SelectItem>
                <SelectItem value="month">Este mes</SelectItem>
                <SelectItem value="quarter">Este trimestre</SelectItem>
                <SelectItem value="year">Este año</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefetching}>
              <RefreshCw className={cn("w-4 h-4 mr-2", isRefetching && "animate-spin")} />
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('pdf')}>
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('excel')}>
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Cargando datos...</span>
          </div>
        )}

        {/* KPIs de Órdenes de Trabajo */}
        {kpiData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Órdenes Totales"
              value={kpiData.financial.workOrders.total}
              previousValue={kpiData.financial.previousMonth.total || 1}
              target={Math.max(kpiData.financial.workOrders.total, kpiData.financial.previousMonth.total) * 1.1}
              format="number"
              icon={ClipboardList}
              color="blue"
            />
            <KPICard
              title="Completadas"
              value={kpiData.financial.workOrders.completed}
              previousValue={kpiData.financial.previousMonth.completed || 1}
              target={kpiData.financial.workOrders.total}
              format="number"
              icon={CheckCircle}
              color="green"
            />
            <KPICard
              title="Tasa de Cumplimiento"
              value={kpiData.financial.workOrders.completionRate}
              previousValue={kpiData.financial.previousMonth.total > 0
                ? (kpiData.financial.previousMonth.completed / kpiData.financial.previousMonth.total) * 100
                : 0}
              target={100}
              format="percent"
              icon={Target}
              color="primary"
            />
            <KPICard
              title="Tasa de Calidad"
              value={kpiData.quality.passRate}
              previousValue={kpiData.quality.passRate > 0 ? kpiData.quality.passRate - 2 : 0}
              target={100}
              format="percent"
              icon={TrendingUp}
              color="green"
            />
          </div>
        )}

        {/* Alertas Inteligentes */}
        {kpiData && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-500" />
                  <CardTitle>Alertas Inteligentes</CardTitle>
                </div>
                <Badge variant="outline">{kpiData.alerts.length} activas</Badge>
              </div>
              <CardDescription>Sistema de predicción y alertas tempranas basado en análisis de datos reales</CardDescription>
            </CardHeader>
            <CardContent>
              {kpiData.alerts.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>Sin alertas activas. Todo funcionando correctamente.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {kpiData.alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className={cn(
                        "p-4 rounded-lg border-l-4 bg-muted/30",
                        alert.type === 'critical' && "border-l-red-500",
                        alert.type === 'warning' && "border-l-yellow-500",
                        alert.type === 'info' && "border-l-blue-500",
                        alert.type === 'success' && "border-l-green-500",
                      )}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {alert.type === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                            {alert.type === 'warning' && <AlertTriangle className="w-4 h-4 text-yellow-500" />}
                            {alert.type === 'info' && <Activity className="w-4 h-4 text-blue-500" />}
                            {alert.type === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                            <span className="font-medium text-sm">{alert.title}</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{alert.message}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <Badge variant="secondary" className="text-xs">{alert.prediction}</Badge>
                            <span className="text-xs text-muted-foreground">hace {alert.time}</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-xs gap-1"
                          onClick={() => handleAlertAction(alert)}
                        >
                          {alert.actionType === 'create_order' && <ShoppingCart className="w-3 h-3" />}
                          {alert.actionType === 'schedule_maintenance' && <Wrench className="w-3 h-3" />}
                          {alert.actionType === 'navigate' && <ExternalLink className="w-3 h-3" />}
                          {alert.actionType === 'view_details' && <ExternalLink className="w-3 h-3" />}
                          {alert.action}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Valor de Inventario - Solo visible para admin/superadmin */}
        {kpiData && canSeeMoney && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-500" />
                <CardTitle>Valor de Inventario</CardTitle>
              </div>
              <CardDescription>Valor estimado del inventario total (Materiales + Materias Primas)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-5 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between mb-3">
                    <Package className="w-8 h-8 text-blue-500" />
                    <Badge variant="outline" className="text-blue-600 border-blue-300">Materiales</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">Valor Materiales</p>
                  <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                    €{kpiData.inventory.materialsValue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/20 border border-orange-200 dark:border-orange-800">
                  <div className="flex items-center justify-between mb-3">
                    <Layers className="w-8 h-8 text-orange-500" />
                    <Badge variant="outline" className="text-orange-600 border-orange-300">Materias Primas</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">Valor Materias Primas</p>
                  <p className="text-2xl font-bold text-orange-700 dark:text-orange-400">
                    €{kpiData.inventory.rawMaterialsValue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                  <div className="flex items-center justify-between mb-3">
                    <Wallet className="w-8 h-8 text-emerald-500" />
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">Total</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">Valor Total Inventario</p>
                  <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">
                    €{kpiData.inventory.totalInventoryValue.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {kpiData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Gráfico de Tendencias de Órdenes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Tendencia de Órdenes (6 meses)
                </CardTitle>
                <CardDescription>Evolución de órdenes totales, completadas y tasa de cumplimiento</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={kpiData.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-xs" />
                    <YAxis yAxisId="left" className="text-xs" />
                    <YAxis yAxisId="right" orientation="right" className="text-xs" tickFormatter={(v) => `${v}%`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
                      formatter={(value: number, name: string) => {
                        if (name === 'completionRate') return [`${value.toFixed(1)}%`, 'Cumplimiento'];
                        return [value, name === 'orders' ? 'Total' : 'Completadas'];
                      }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="orders" fill="hsl(var(--primary))" name="Total" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="left" dataKey="completed" fill="#22c55e" name="Completadas" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="completionRate" stroke="#8b5cf6" strokeWidth={3} name="Cumplimiento %" dot={{ fill: '#8b5cf6' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Métricas de Calidad */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gauge className="w-5 h-5" />
                  Métricas de Calidad
                </CardTitle>
                <CardDescription>Inspecciones y tasa de aprobación</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center">
                  <div className="relative w-32 h-32 mb-4">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="none" className="text-muted/20" />
                      <circle
                        cx="64" cy="64" r="56"
                        stroke="#22c55e"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${(kpiData.quality.passRate / 100) * 351.86} 351.86`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold">{kpiData.quality.passRate.toFixed(1)}%</span>
                      <span className="text-xs text-muted-foreground">Tasa Aprobación</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 w-full">
                    <OEEGauge value={kpiData.quality.totalInspections > 0 ? 100 : 0} label={`${kpiData.quality.totalInspections} Total`} color="#3b82f6" />
                    <OEEGauge value={kpiData.quality.totalInspections > 0 ? (kpiData.quality.passed / kpiData.quality.totalInspections) * 100 : 0} label={`${kpiData.quality.passed} Aprobadas`} color="#22c55e" />
                    <OEEGauge value={kpiData.quality.totalInspections > 0 ? (kpiData.quality.failed / kpiData.quality.totalInspections) * 100 : 0} label={`${kpiData.quality.failed} Fallidas`} color="#ef4444" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {kpiData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribución por Cliente */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChartIcon className="w-5 h-5" />
                  Órdenes por Cliente
                </CardTitle>
                <CardDescription>Distribución de órdenes de trabajo por cliente</CardDescription>
              </CardHeader>
              <CardContent>
                {kpiData.customers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Sin datos de clientes disponibles
                  </div>
                ) : (
                  <div className="flex items-center">
                    <ResponsiveContainer width="50%" height={250}>
                      <PieChart>
                        <Pie
                          data={kpiData.customers}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="orderCount"
                        >
                          {kpiData.customers.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value: number) => `${value} OT`} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-2">
                      {kpiData.customers.map((customer) => (
                        <div key={customer.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: customer.color }} />
                            <span className="text-sm truncate max-w-[120px]">{customer.name}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium">{customer.orderCount} OT</span>
                            <span className="text-xs text-muted-foreground ml-2">({customer.completedCount} comp.)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Producción Semanal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Boxes className="w-5 h-5" />
                  Producción Semanal
                </CardTitle>
                <CardDescription>Unidades producidas vs objetivo (últimos 7 días)</CardDescription>
              </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={kpiData.production.weeklyProduction}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="day" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                  <Legend />
                  <Bar dataKey="produced" fill="hsl(var(--primary))" name="Producidas" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="target" fill="hsl(var(--muted-foreground))" name="Objetivo" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        )}

        {/* Resumen de Estado */}
        {kpiData && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Resumen del Período
              </CardTitle>
              <CardDescription>Métricas clave basadas en datos reales del sistema</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-muted/30 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">OTs Este Mes</span>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-2xl font-bold">{kpiData.financial.currentMonth.total}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Progress value={(kpiData.financial.currentMonth.completed / Math.max(kpiData.financial.currentMonth.total, 1)) * 100} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground">{kpiData.financial.currentMonth.completed} completadas</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/30 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Entregas a Tiempo</span>
                    {kpiData.financial.workOrders.onTimeRate >= 80 ? (
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <p className="text-2xl font-bold">{kpiData.financial.workOrders.onTimeRate.toFixed(1)}%</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Progress value={kpiData.financial.workOrders.onTimeRate} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground">objetivo: 95%</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/30 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Tasa de Calidad</span>
                    {kpiData.quality.passRate >= 95 ? (
                      <TrendingUp className="w-4 h-4 text-green-500" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <p className="text-2xl font-bold">{kpiData.quality.passRate.toFixed(1)}%</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Progress value={kpiData.quality.passRate} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground">{kpiData.quality.totalDefects} defectos</span>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/30 border">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">En Progreso</span>
                    <Activity className="w-4 h-4 text-blue-500" />
                  </div>
                  <p className="text-2xl font-bold">{kpiData.financial.workOrders.inProgress}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Progress value={(kpiData.financial.workOrders.inProgress / Math.max(kpiData.financial.workOrders.total, 1)) * 100} className="h-1.5 flex-1" />
                    <span className="text-xs text-muted-foreground">{kpiData.financial.workOrders.pending} pendientes</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer con última actualización */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Última actualización: {kpiData?.lastUpdated
              ? format(kpiData.lastUpdated, "dd MMM yyyy 'a las' HH:mm", { locale: es })
              : format(new Date(), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
          </span>
          <span className="flex items-center gap-2">
            <div className={cn("w-2 h-2 rounded-full", isLoading || isRefetching ? "bg-yellow-500 animate-pulse" : "bg-green-500 animate-pulse")} />
            {isLoading || isRefetching ? 'Actualizando...' : 'Datos en tiempo real'}
          </span>
        </div>
      </div>

      {/* Diálogo: Generar Pedido de Material */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary" />
              Generar Pedido de Material
            </DialogTitle>
            <DialogDescription>
              Crear un pedido para {selectedAlert?.materialName || 'material'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="material">Material</Label>
              <Input
                id="material"
                value={selectedAlert?.materialName || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="quantity">Cantidad (m²)</Label>
              <Input
                id="quantity"
                type="number"
                placeholder="Ingrese la cantidad"
                value={orderQuantity}
                onChange={(e) => setOrderQuantity(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="order-notes">Notas (opcional)</Label>
              <Textarea
                id="order-notes"
                placeholder="Notas adicionales para el pedido..."
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateOrder} className="bg-primary">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Generar Pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Programar Mantenimiento */}
      <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-primary" />
              Programar Mantenimiento
            </DialogTitle>
            <DialogDescription>
              Programar mantenimiento para {selectedAlert?.machineCode || 'equipo'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="machine">Equipo</Label>
              <Input
                id="machine"
                value={selectedAlert?.machineCode || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maintenance-date">Fecha de Mantenimiento</Label>
              <Input
                id="maintenance-date"
                type="date"
                value={maintenanceDate}
                onChange={(e) => setMaintenanceDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="maintenance-notes">Descripción del Trabajo</Label>
              <Textarea
                id="maintenance-notes"
                placeholder="Descripción del mantenimiento a realizar..."
                value={maintenanceNotes}
                onChange={(e) => setMaintenanceNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleScheduleMaintenance} className="bg-primary">
              <Wrench className="w-4 h-4 mr-2" />
              Programar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
